
import { getProductLocation, getFinisherByName, getProductModel } from '@/api';
import { Header, ProductLocation, StopNames } from '@/models/main';
import { JBAdvancedStops, JBGoodsTableData, JBGoodsTableDataFull } from '@/models/jbAdvanced';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Autocomplete from './Autocomplete.vue';
import AutocompleteFinisher from './AutocompleteFinisher.vue';

import {
  installationOptions,
  installationOptionsText,
  returnOptions,
  returnOptionsList,
  returnOptionsTexts,
} from '@/utils/PortalConstants';

@Component({
  components: {
    Autocomplete,
    AutocompleteFinisher,
  },
  data() {
    return {
      installationOptions,
      installationOptionsText,
      returnOptions,
      returnOptionsList,
      returnOptionsTexts,
    };
  },
})
export default class Goods extends Vue {
  @Prop(Number) private readonly goodNumber: number;
  @Prop(Number) private readonly goodsOnFirstLegCounter: number;
  @Prop(Object) private readonly stopNames: StopNames;
  @Prop(Array) private readonly stops: JBAdvancedStops;
  @Prop(String) private readonly customerCode: string;
  @Prop(Object) private readonly goodsData: JBGoodsTableDataFull;

  private autocompleteModel = getProductModel;
  private tabItemsPrevCount = 66; // 66 actually for now

  private tableHeaders: Header[] = [
    { text: 'Model', value: 'model' },
    { text: 'Description', value: 'description' },
    { text: 'Serial number', value: 'serial_number' },
    { text: 'Quantity', value: 'quantity' },
    { text: 'Installation / service', value: 'installation' },
    { text: 'Return option', value: 'return' },
  ];
  private defaultTableValue: JBGoodsTableData = {
    id: 0,
    product_id: 0,
    model: '',
    description: '',
    serial_number: '',
    quantity: 1,
    installation: '',
    returnOption: '',
    returnOptionText: '',
    serialNumberFilled: false,
    statusText: '',
  };
  private goods: JBGoodsTableDataFull = {
    tableData: [{ ...this.defaultTableValue }],
    checkboxStairs: false,
    checkboxLoadingDock: false,
    checkboxPPE: false,
    checkboxRunup: false,
    radioTypeGoods: 'free',
    stairsNotes: '',
  };

  private showStairsNotes = false;
  private stopGoodsCounter = 1;

  // endpoints
  private changeModel(stop, item) {
    this.goods.tableData[stop].model = item.name;
    this.goods.tableData[stop].product_id = item.product_id;
  }

  /**
   * Used from child components by emit()
   * @param stop
   * @private
   */
  private clear(stop: number) {
    this.goods.tableData[stop].model = '';
    this.goods.tableData[stop].product_id = 0;
    this.goods.tableData[stop].serialNumberFilled = false;
    this.goods.tableData[stop].statusText = '';
  }

  private blur(event, stop) {
    const product_id = this.goods.tableData[stop].product_id;
    const serial_number = event.target.value;
    this.goods.tableData[stop].serialNumberFilled = false;
    this.goods.tableData[stop].statusText = '';

    if (product_id && serial_number) {
      getProductLocation(product_id, serial_number)
        .then((res: ProductLocation) => {
          let inputText = '';
          if (this.goods.tableData[stop].id === stop) {
            if (Object.keys(res).length >= 1) {
              inputText = `Warehouse: ${res.warehouse}, Status: ${res.name}`;
            } else {
              inputText = 'Out of stock';
            }
            this.goods.tableData[stop].serialNumberFilled = true;
            this.goods.tableData[stop].quantity = 1;
            this.goods.tableData[stop].statusText = inputText;
          }
        })
        .catch(() => {
          this.goods.tableData[stop].statusText = 'Out of stock';
        });
    }
  }

  private addMachine(): void {
    let newGood = {
      ...this.defaultTableValue,
      id: this.goods.tableData.length,
    };
    if (this.goodNumber === 2 && !this.stops[2].warehouse) {
      newGood = {
        ...newGood,
        installation: 'DLVONLY',
      };
    }
    this.goods.tableData.push(newGood);
  }

  private addFinisher(): void {
    const lastIndex = this.goods.tableData.length - 1;
    this.goods.tableData.push({
      ...this.defaultTableValue,
      id: this.goods.tableData.length,
      model: '',
      product_id: -1,
      installation: this.goods.tableData[lastIndex].installation,
      returnOption: this.goods.tableData[lastIndex].returnOption,
      returnOptionText: this.goods.tableData[lastIndex].returnOptionText,
    });
  }

  private async autocompleteFinisher(model, customer) {
    let responce = await getFinisherByName(model, customer);
    // TODO: change default name & id?
    responce = [...responce, { name: 'AA-FINISHER', product_id: 6182 }];
    return responce;
  }

  private deleteItem(item: JBGoodsTableData): void {
    this.goods.tableData.splice(this.goods.tableData.indexOf(item), 1);
  }

  @Watch('goodsData', { immediate: true, deep: true })
  private watchToGoodsData(): void {
    if (this.goods && this.goodsData) {
      this.goods = this.goodsData;
      this.watchToStops();
    }
  }

  @Watch('goods', { deep: true })
  private watchToGoods(): void {
    // counting goods. the limit is 36 printers/finishers
    if (this.goods && this.goods.tableData) {
      this.stopGoodsCounter = this.goods?.tableData?.length + 1;
      this.$emit('changeGoods', this.goods, this.goodNumber);
      if (
        this.goods.checkboxStairs ||
        this.goods.checkboxLoadingDock ||
        this.goods.checkboxPPE ||
        this.goods.checkboxRunup
      ) {
        this.showStairsNotes = true;
      } else {
        this.showStairsNotes = false;
      }
    }
  }

  private returnOptionChanged(value, idx): void {
    const optionIdx = returnOptionsList.indexOf(value);
    this.goods.tableData[idx].returnOptionText = returnOptionsTexts[optionIdx];
  }

  @Watch('stops', { immediate: true, deep: true })
  private watchToStops(): void {
    // if stop 1 is warehouse – show installation
    const installationIndex = this.tableHeaders.findIndex(header => header.value === 'installation');
    if (
      (!this.stops[1]?.warehouse && installationIndex === -1 && this.goodNumber === 1) ||
      (!this.stops[0]?.warehouse && !this.stops[1]?.warehouse && installationIndex === -1 && this.goodNumber !== 2)
    ) {
      this.tableHeaders.splice(5, 0, { text: 'Installation / service', value: 'installation' });
    } else {
      if (
        (!this.stops[0]?.warehouse && this.stops[1]?.warehouse && installationIndex >= 0) ||
        (this.stops[2]?.warehouse && this.goodNumber === 2)
      ) {
        this.tableHeaders.splice(installationIndex, 1);
        this.goods.tableData.map(product => {
          product.installation = '';
        });
      }
      // WH -> Customer -> Customer case
      if (!this.stops[2]?.warehouse && this.goodNumber === 2 && installationIndex === -1) {
        this.tableHeaders.splice(5, 0, { text: 'Installation / service', value: 'installation' });
        this.goods.tableData.map(product => {
          product.installation = 'DLVONLY';
        });
      }
    }
    // if stop 2 / 3 is warehouse – return options on leg 1 / leg 2
    const returnIndex = this.tableHeaders.findIndex(header => header.value === 'return');
    if (this.stops[this.goodNumber]?.warehouse && returnIndex === -1) {
      this.tableHeaders.splice(5, 0, { text: 'Return option', value: 'return' });
    } else {
      if (!this.stops[this.goodNumber]?.warehouse && returnIndex >= 0) {
        this.tableHeaders.splice(returnIndex, 1);
        this.goods.tableData.map(product => {
          product.returnOption = '';
          product.returnOptionText = '';
        });
      }
    }
  }

  private decrementQuantity(item: JBGoodsTableData): void {
    if (
      !this.goods.tableData[this.goods.tableData.indexOf(item)].serialNumberFilled &&
      this.goods.tableData[this.goods.tableData.indexOf(item)].quantity > 1
    ) {
      this.goods.tableData[this.goods.tableData.indexOf(item)].quantity -= 1;
    }
  }

  private incrementQuantity(item: JBGoodsTableData): void {
    if (!this.goods.tableData[this.goods.tableData.indexOf(item)].serialNumberFilled) {
      this.goods.tableData[this.goods.tableData.indexOf(item)].quantity += 1;
    }
  }
}
